import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  DEFAULT_ENV,
  ENVS,
  ENV_STORAGE_KEY,
  isEnvName,
  type EnvConfig,
  type EnvName,
} from "../config/envs";

interface EnvContextValue {
  env: EnvConfig;
  setEnv: (next: EnvName) => void;
}

const EnvContext = createContext<EnvContextValue | null>(null);

function readStoredEnv(): EnvName {
  try {
    const raw = window.localStorage.getItem(ENV_STORAGE_KEY);
    if (isEnvName(raw)) return raw;
  } catch {
    // localStorage may throw in private mode; fall through to default.
  }
  return DEFAULT_ENV;
}

export function EnvProvider({ children }: { children: ReactNode }) {
  const [name, setName] = useState<EnvName>(readStoredEnv);

  const setEnv = useCallback((next: EnvName) => {
    setName(next);
    try {
      window.localStorage.setItem(ENV_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<EnvContextValue>(
    () => ({ env: ENVS[name], setEnv }),
    [name, setEnv],
  );

  return <EnvContext.Provider value={value}>{children}</EnvContext.Provider>;
}

export function useEnv(): EnvConfig {
  const ctx = useContext(EnvContext);
  if (!ctx) throw new Error("useEnv must be used inside <EnvProvider>");
  return ctx.env;
}

export function useSetEnv(): (next: EnvName) => void {
  const ctx = useContext(EnvContext);
  if (!ctx) throw new Error("useSetEnv must be used inside <EnvProvider>");
  return ctx.setEnv;
}

export function useApiBaseUrl(): string {
  return useEnv().apiBaseUrl;
}
